import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar, ChevronDown, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ReservationSection = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1); // 1: date/time, 2: contact, 3: confirmation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    guests: '2',
    date: '',
    time: '',
    name: '',
    email: '',
    phone: '',
    message: '',
    newsletter: false
  });

  const lunchSlots = ['11h30', '12h00', '12h30', '13h00', '13h30'];
  const dinnerSlots = ['19h30', '20h00', '20h30', '21h00', '21h30', '22h00'];

  const guestOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  // French date formatting
  const formatDateFrench = (dateStr) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const date = new Date(dateStr + 'T00:00:00');
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    return `${dayName} ${date.getDate()} ${monthName} ${date.getFullYear()}`;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTimeSelect = (time) => {
    setFormData({ ...formData, time });
  };

  const canProceedToStep2 = () => {
    return formData.guests && formData.date && formData.time;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Convert time format back for API (19h30 -> 19:30)
      const timeForApi = formData.time.replace('h', ':');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reservations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          guests: parseInt(formData.guests),
          date: formData.date,
          time: timeForApi,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          newsletter: formData.newsletter
        })
      });

      if (response.ok) {
        // Show confirmation screen
        setStep(3);
      } else {
        throw new Error('Reservation failed');
      }
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de créer la réservation. Veuillez réessayer.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  const handleReturnHome = () => {
    // Reset form and go back to step 1
    setFormData({
      guests: '2',
      date: '',
      time: '',
      name: '',
      email: '',
      phone: '',
      message: '',
      newsletter: false
    });
    setStep(1);
    setIsSubmitting(false);
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Step 3: Confirmation Screen
  if (step === 3) {
    return (
      <section id="reservation" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-lg shadow-2xl p-12 md:p-16">
              {/* Success Icon */}
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50">
                  <CheckCircle className="w-12 h-12 text-green-600" strokeWidth={1.5} />
                </div>
              </div>

              {/* Main Message */}
              <h2 
                className="text-2xl md:text-3xl font-light mb-6 text-gray-900"
                style={{ fontFamily: "'Cinzel', serif" }}
              >
                Votre demande de réservation a bien été soumise.
              </h2>

              {/* Divider */}
              <div className="w-16 h-0.5 bg-gray-300 mx-auto mb-8"></div>

              {/* Supporting Text */}
              <div className="space-y-4 mb-10">
                <p className="text-gray-600 leading-relaxed" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Elle sera confirmée après vérification de nos disponibilités.
                </p>
                <p className="text-gray-600 leading-relaxed" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Vous recevrez un email de confirmation très prochainement.
                </p>
                <p className="text-gray-700 leading-relaxed mt-6 italic" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                  Nous vous remercions de votre confiance et avons hâte de vous accueillir.
                </p>
              </div>

              {/* Reservation Summary */}
              <div className="bg-gray-50 rounded-lg p-6 mb-10 text-left">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Date</p>
                    <p className="text-gray-900 font-medium">{formatDateFrench(formData.date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Heure</p>
                    <p className="text-gray-900 font-medium">{formData.time}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Personnes</p>
                    <p className="text-gray-900 font-medium">{formData.guests}</p>
                  </div>
                </div>
              </div>

              {/* Return Button */}
              <Button
                onClick={handleReturnHome}
                className="bg-black text-white hover:bg-gray-800 px-10 py-6 text-base font-normal tracking-wide transition-all duration-300 hover:-translate-y-1 shadow-lg"
                style={{ fontFamily: "'Libre Baskerville', serif" }}
              >
                Retour à l'accueil
              </Button>
            </div>

            {/* Contact Info */}
            <div className="mt-10 text-gray-500 text-sm">
              <p>Une question ? Contactez-nous au <a href="tel:+33450288857" className="text-black hover:underline">+33 4 50 28 88 57</a></p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="reservation" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-6xl font-bold mb-6 text-gray-900">
              Réservation
            </h2>
            <div className="w-20 h-1 bg-black mx-auto mb-6"></div>
            <p className="text-xl text-gray-700">
              Réservez votre table pour une expérience gastronomique mémorable
            </p>
          </div>

          {step === 1 ? (
            <div className="bg-white rounded-lg shadow-2xl p-8 md:p-12">
              <div className="space-y-8">
                {/* Guest Selection */}
                <div>
                  <Label className="text-lg font-semibold mb-4 block text-gray-900">
                    Nombre de personnes
                  </Label>
                  <div className="relative">
                    <select
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      className="w-full h-16 text-lg font-semibold border-2 border-gray-300 rounded-lg px-4 pr-10 appearance-none focus:outline-none focus:border-black transition-colors cursor-pointer bg-white"
                    >
                      {guestOptions.map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? 'personne' : 'personnes'}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none" size={24} />
                  </div>
                </div>

                {/* Date Selection */}
                <div>
                  <Label htmlFor="date" className="text-lg font-semibold mb-4 block text-gray-900">
                    Sélectionnez une date
                  </Label>
                  <div className="relative">
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      required
                      value={formData.date}
                      onChange={handleChange}
                      className="h-16 text-lg border-2 border-gray-300 focus:border-black transition-colors pl-12"
                      min={new Date().toISOString().split('T')[0]}
                    />
                    <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 pointer-events-none" size={24} />
                  </div>
                  {formData.date && (
                    <p className="mt-2 text-gray-600 text-sm">{formatDateFrench(formData.date)}</p>
                  )}
                </div>

                {/* Time Selection - Lunch */}
                {formData.date && (
                  <div>
                    <Label className="text-lg font-semibold mb-4 block text-gray-900">
                      Le Midi
                    </Label>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {lunchSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => handleTimeSelect(time)}
                          className={`h-14 rounded-lg border-2 font-semibold text-base transition-all duration-200 hover:scale-105 ${
                            formData.time === time
                              ? 'bg-black text-white border-black'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time Selection - Dinner */}
                {formData.date && (
                  <div>
                    <Label className="text-lg font-semibold mb-4 block text-gray-900">
                      Le Soir
                    </Label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {dinnerSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => handleTimeSelect(time)}
                          className={`h-14 rounded-lg border-2 font-semibold text-base transition-all duration-200 hover:scale-105 ${
                            formData.time === time
                              ? 'bg-black text-white border-black'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Continue Button */}
                <Button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={!canProceedToStep2()}
                  className="w-full bg-black text-white hover:bg-gray-800 h-16 text-lg font-semibold transition-all duration-300 hover:-translate-y-1 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  Continuer la réservation
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-2xl p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Reservation Summary */}
                <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
                  <h3 className="font-semibold text-xl mb-6 text-gray-900 text-center">
                    Récapitulatif de votre réservation
                  </h3>
                  <div className="grid grid-cols-3 gap-6 text-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-2 uppercase tracking-wide">Personnes</p>
                      <p className="font-bold text-2xl text-gray-900">{formData.guests}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2 uppercase tracking-wide">Date</p>
                      <p className="font-bold text-base text-gray-900">
                        {formatDateFrench(formData.date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-2 uppercase tracking-wide">Heure</p>
                      <p className="font-bold text-2xl text-gray-900">{formData.time}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="h-14 text-base border-2 border-gray-300 focus:border-black transition-colors"
                        placeholder="Votre nom"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="h-14 text-base border-2 border-gray-300 focus:border-black transition-colors"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                      Téléphone *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-14 text-base border-2 border-gray-300 focus:border-black transition-colors"
                      placeholder="+33 X XX XX XX XX"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-base font-semibold mb-2 block">
                      Demandes spéciales (optionnel)
                    </Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full min-h-32 text-base border-2 border-gray-300 rounded-md px-4 py-3 focus:outline-none focus:border-black transition-colors"
                      placeholder="Allergies, préférences de placement, occasion spéciale..."
                    />
                  </div>

                  {/* Newsletter Consent */}
                  <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <input
                      type="checkbox"
                      id="newsletter"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={(e) => setFormData({ ...formData, newsletter: e.target.checked })}
                      className="w-5 h-5 mt-0.5 rounded border-gray-300 text-black focus:ring-black focus:ring-2 cursor-pointer"
                    />
                    <label htmlFor="newsletter" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                      J&apos;accepte de recevoir les newsletters et offres promotionnelles de La Rotonde par email.
                      <span className="block text-xs text-gray-500 mt-1">
                        Vous pouvez vous désinscrire à tout moment.
                      </span>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    disabled={isSubmitting}
                    className="flex-1 border-2 border-black text-black hover:bg-gray-100 h-14 text-base font-semibold transition-all duration-300"
                  >
                    Retour
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-black text-white hover:bg-gray-800 h-14 text-base font-semibold transition-all duration-300 hover:-translate-y-1 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Confirmer la réservation'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 text-base mb-3">
              Pour les groupes de plus de 10 personnes, veuillez nous contacter directement.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-base">
              <a href="tel:+33450288857" className="text-black font-semibold hover:underline">
                +33 4 50 28 88 57
              </a>
              <span className="hidden sm:inline text-gray-400">|</span>
              <a
                href="mailto:larotonde.divonne@gmail.com"
                className="hover:underline font-semibold text-black"
              >
                larotonde.divonne@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReservationSection;
